/**
 * Digital Signage Screen 2 — Wydarzenia
 * Same glass-card style as site, big fonts for 55" at 2-3m
 * Vertical list with 1:1 images, auto-pagination
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, Repeat, Tag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_INTERVAL = 5 * 60 * 1000;
const SLIDE_INTERVAL = 10000;
const DAY_PL = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
const MONTH_PL = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'];

const DSScreen2 = () => {
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(0);
  const [fade, setFade] = useState(true);

  const fetchData = async () => {
    try { const r = await axios.get(`${API}/api/events`); setEvents(r.data || []); } catch (e) {} };

  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_INTERVAL); return () => clearInterval(i); }, []);
  useEffect(() => {
    const PER_PAGE = 4;
    const tp = Math.ceil(events.length / PER_PAGE);
    if (tp <= 1) return;
    const i = setInterval(() => { setFade(false); setTimeout(() => { setPage(p => (p + 1) % tp); setFade(true); }, 500); }, SLIDE_INTERVAL);
    return () => clearInterval(i);
  }, [events.length]);

  const ls = (f) => (!f ? '' : typeof f === 'string' ? f : f.pl || f.en || Object.values(f)[0] || '');
  const fmtDate = (d) => { const dt = new Date(d); return `${DAY_PL[dt.getDay()]}, ${dt.getDate()} ${MONTH_PL[dt.getMonth()]}`; };
  const fmtDays = (days) => (days || []).sort((a, b) => a - b).map(d => DAY_PL[d]).join(', ');
  const bl = (b) => (typeof b === 'string' ? b : b?.pl || b?.en || '');

  if (!events.length) return (
    <div className="w-screen h-screen flex items-center justify-center" style={{ background: '#050505' }}>
      <div className="text-center">
        <Calendar className="w-20 h-20 text-primary/20 mx-auto mb-6" />
        <p className="text-3xl font-['Syne'] font-bold text-white/20">Brak nadchodzących wydarzeń</p>
      </div>
    </div>
  );

  const PER_PAGE = 4;
  const totalPages = Math.ceil(events.length / PER_PAGE);
  const visible = events.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  return (
    <div
      className="w-screen h-screen overflow-hidden flex flex-col px-8 py-6"
      style={{ background: '#050505' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-shrink-0">
        <h1 className="text-4xl font-['Syne'] font-bold text-white">Nadchodzące wydarzenia</h1>
        {totalPages > 1 && (
          <div className="flex gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <div key={i} className={`h-2.5 rounded-full transition-all duration-300 ${i === page ? 'w-8 bg-primary' : 'w-2.5 bg-white/10'}`} />
            ))}
          </div>
        )}
      </div>

      {/* Events list */}
      <div className={`flex-1 flex flex-col gap-4 min-h-0 justify-start transition-opacity duration-500 ${fade ? 'opacity-100' : 'opacity-0'}`}>
        {visible.map((event) => {
          const img = event.image ? (event.image.startsWith('/') ? `${API}${event.image}` : event.image) : null;
          const isFree = event.price === 0 || event.price == null;

          return (
            <div key={event.id || event.slug} className="glass-card flex items-center gap-7 px-6 min-h-0" style={{ height: visible.length >= 4 ? undefined : 'auto', flex: visible.length >= 4 ? '1' : 'none', padding: '20px 24px' }}>
              {/* Square image */}
              <div className="w-40 h-40 rounded-2xl overflow-hidden flex-shrink-0 bg-white/5">
                {img ? (
                  <img src={img} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Calendar className="w-10 h-10 text-white/10" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col justify-center gap-2 min-w-0 py-4">
                {/* Badges */}
                {event.badges?.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {event.badges.map((b, i) => (
                      <span key={i} className="px-4 py-1 rounded-full text-sm font-bold bg-primary/15 text-primary border border-primary/25">
                        {bl(b)}
                      </span>
                    ))}
                    {event.isRecurring && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-white/5 text-white/25 border border-white/10">
                        <Repeat className="w-3.5 h-3.5" /> Cykliczne
                      </span>
                    )}
                  </div>
                )}

                {/* Title */}
                <h2 className="text-3xl font-['Syne'] font-bold text-white leading-tight truncate">
                  {ls(event.title)}
                </h2>

                {/* Meta row */}
                <div className="flex items-center gap-8 mt-1">
                  <div className="flex items-center gap-2.5">
                    <Calendar className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-xl font-semibold text-white/55">
                      {event.isRecurring ? `Każdy ${fmtDays(event.recurringConfig?.daysOfWeek)}` : fmtDate(event.nextDate || event.date)}
                    </span>
                  </div>
                  {event.time && (
                    <div className="flex items-center gap-2.5">
                      <Clock className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-xl font-semibold text-white/55">
                        {event.time}{event.endTime ? ` — ${event.endTime}` : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Price — far right, big */}
              <div className="flex-shrink-0 text-right pl-6">
                <div className="text-sm font-medium text-white/25 mb-1">Wstęp</div>
                <div className={`text-4xl font-['Syne'] font-bold ${isFree ? 'text-green-400' : 'text-white'}`}>
                  {isFree ? 'Bezpłatne' : `${event.price} zł`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DSScreen2;
